import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';

import { HttpError, HttpTimeoutError } from './errors';
import { HttpOptions } from './interfaces';
import { NetworkMonitorService } from './network-monitor.service';
import { HttpClientAdapter } from './http-client-adapter';
import { IonicHttpClient } from './client/ionic-http-client';
import { AngularHttpClient } from './client/angular-http-client';

import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/retryWhen';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/timeoutWith';
import { ConfigurationManager } from '../provider/ConfigurationManager';

@Injectable()
export class HttpClientService extends HttpClientAdapter {

  private errors: ReplaySubject<HttpError> = new ReplaySubject();

  private defaultTimeout = 15000;
  private defaultRetries = 1;
  private defaultRetryDelay = 500;


  constructor(
    private networkMonitor: NetworkMonitorService,
    private adapter: HttpClientAdapter,
    private config: ConfigurationManager
  ) {
    super();

   // this.defaultTimeout = this.httpTimeout;
   // this.defaultRetries = this.httpRetries;
   // this.defaultRetryDelay = this.httpRetryDelay;
  }

  get(url: string, params: { [name: string]: string }, options: HttpOptions = {}): Observable<any> {
   // this.logger.debug('GET', url, params, options);
    return this.monitor(this.adapter.get(url, params, options), options);
  }

  post(url: string, params: { [name: string]: string }, body: any | null, options: HttpOptions = {}): Observable<any> {
  //  this.logger.debug('POST', url, params, body, options);
    return this.monitor(this.adapter.post(url, params, body, options), options);
  }

  put(url: string, params: { [name: string]: string }, body: any | null, options: HttpOptions = {}): Observable<any> {
   // this.logger.debug('PUT', url, params, body, options);
    return this.monitor(this.adapter.put(url, params, body, options), options);
  }

  patch(url: string, params: { [name: string]: string }, body: any | null, options: HttpOptions = {}): Observable<any> {
  //  this.logger.debug('PATCH', url, params, body, options);
    return this.monitor(this.adapter.patch(url, params, body, options), options);
  }

  delete(url: string, params: { [name: string]: string }, options: HttpOptions = {}): Observable<any> {
   // this.logger.debug('DELETE', url, params, options);
    return this.monitor(this.adapter.delete(url, params, options), options);
  }

  public getErrors() {
    return this.errors.asObservable();
  }

  public setHttpClientAdapter(adapter: HttpClientAdapter) {
    this.adapter = adapter;
  }

  /**
   * Wraps an observable with retry, timeout and network detection.
   *
   * @param observable Observable
   */
  public monitor(observable: Observable<any>, options: HttpOptions = {}): Observable<any> {
    const ignoreRetryOnStatuses = [200, 404, 401, 403];

    return this.networkMonitor.isConnected()
      ? observable
          .timeoutWith((options.timeout || this.defaultTimeout), Observable.throw(new HttpTimeoutError()))
          .retryWhen(errors => {
            return errors
              .flatMap((err, count) => (
                count < (options.retries || this.defaultRetries) && ignoreRetryOnStatuses.indexOf(err.status) === -1
                  ? Observable.of(err)
                  : Observable.throw(err)
              ))
              .delay(options.retryDelay || this.defaultRetryDelay)
            ;
          })
          .catch((err) => {
            const newError = <HttpError>{
              message: err.message,
              status: err.status,
              type: 'server',
              debug: err
            };

           // this.logger.debug('HTTP ERROR', newError);

            this.errors.next(newError);

            return Observable.throw(newError);
          })
          .do(x => x)

      : this.throwNetworkError(observable)
    ;
  }


  private throwNetworkError(observable: Observable<any>): Observable<any> {
    return Observable.throw(<HttpError>{
      type: 'network',
      error: 'No network connection',
      message: 'No network connection',
      status: 0,
    });
  }
}

export function httpClientAdapterFactory(
  config: ConfigurationManager,
  angularHttpClient: AngularHttpClient,
  ionicHttpClient: IonicHttpClient,
) {
  return config.getEnvironmentName === 'PROD' ? ionicHttpClient : angularHttpClient;
}

/**
 * Provides the correct HttpClientAdapter for the environment
 */
export const HttpClientAdapterInitializer = {
  provide: HttpClientAdapter,
  useFactory: httpClientAdapterFactory,
  deps: [ ConfigurationManager, AngularHttpClient, IonicHttpClient ],
};
